'use client'

import { GRADE_COLORS } from '@/lib/radar/scoring'
import {
  categoryControlLabel,
  summariseCategories,
} from '@/lib/radar/categories'
import type { ToolKey, ToolState } from '@/types/radar'
import { GradeChip, InstrumentLabel, Panel } from './ui'

/**
 * The five AI-readiness pillars, in two pieces that are read together:
 *
 *   `SignalProfile`    — the radar polygon, one column of the dashboard hero.
 *   `SignalDetailBand` — the same five pillars as a full-width strip beneath,
 *                        with grade, bar and the tools behind each mean.
 *
 * They are separate components because they are separate SHAPES, not because
 * they are separate data. The detail started out stacked under the chart in
 * the hero column, which pushed that column past 700px against ~330px
 * neighbours; splitting it puts the detail where it has width to be read and
 * lets the hero stay a row of three comparable panels.
 *
 * Dashboard-only. `CategoryGrid` renders the same `summariseCategories` data
 * as a card grid and is imported by the marketing site (RadarDemo), so these
 * live beside it rather than as a variant of it — the homepage must not gain
 * a chart because the dashboard wanted one.
 *
 * Hand-rolled SVG for the same reason `TrendSparkline` is: five points do not
 * justify a charting dependency, and a polygon is a handful of trig.
 *
 * Accessibility: the SVG carries a text alternative naming every pillar and
 * its score, and the geometry inside it is hidden so a reader is never walked
 * through five polygons and five lines. Every figure in it is also rendered as
 * text in the band — the chart is never the only source of a number.
 */

/**
 * viewBox. Wider than tall because the annotations sit OUTSIDE the polygon:
 * the two side vertices carry the longest labels, so the horizontal margin has
 * to hold "Crawlability" and "Community", not just the stroke.
 */
const VB_W = 380
const VB_H = 312
const CX = VB_W / 2
const CY = 158
const RADIUS = 104
/** Where the label/value pair sits, measured from the centre. */
const LABEL_RADIUS = RADIUS + 30
/** Rings at 25/50/75/100 so a score can be read off the grid approximately. */
const RINGS = [0.25, 0.5, 0.75, 1]

/** Angle for spoke `i` of `n`, starting at 12 o'clock and going clockwise. */
function angle(i: number, n: number): number {
  return (Math.PI * 2 * i) / n - Math.PI / 2
}

function point(i: number, n: number, ratio: number, radius = RADIUS) {
  const a = angle(i, n)
  return {
    x: CX + Math.cos(a) * radius * ratio,
    y: CY + Math.sin(a) * radius * ratio,
    a,
  }
}

function polygon(ratios: number[]): string {
  return ratios
    .map((r, i) => {
      const p = point(i, ratios.length, r)
      return `${p.x},${p.y}`
    })
    .join(' ')
}

/**
 * Which side of the spoke the label hangs off. Derived from the angle rather
 * than hand-placed per vertex, so the ring still reads correctly if a sixth
 * category is ever added — five hard-coded x/y pairs would silently collide.
 */
function anchorFor(cos: number): 'start' | 'middle' | 'end' {
  if (Math.abs(cos) < 0.25) return 'middle'
  return cos > 0 ? 'start' : 'end'
}

export function SignalProfile({
  tools,
  overallScore = null,
  isRunning = false,
}: {
  tools: Partial<Record<ToolKey, ToolState>>
  /** Rendered at the centre of the shape. Omitted when the run is unscored. */
  overallScore?: number | null
  /**
   * The audit is still in flight. Same flag DashboardHero and MetricBand
   * read, so all three describe an in-progress run the same way instead of
   * each inferring it from tool status and getting it wrong.
   */
  isRunning?: boolean
}) {
  const summaries = summariseCategories(tools)

  // A category with no gradeable tool has no ratio to plot. It still gets a
  // spoke and a row so the shape never silently loses a pillar — the value
  // reads as an em-dash and the point sits at the origin.
  const plotted = summaries.map(s => ({
    summary: s,
    key: s.key,
    label: s.label,
    score: s.score,
    grade: s.grade,
    ratio: typeof s.score === 'number' ? Math.max(0, s.score) / 100 : 0,
  }))

  const scored = plotted.filter(p => typeof p.score === 'number')

  // Nothing gradeable means there is no shape to draw. Render the reason
  // instead of a collapsed polygon over five em-dashes — the same rule
  // TrendSparkline follows for a series too short to be a trend.
  //
  // FOUR distinct facts, never collapsed. A run in flight, a domain never
  // audited, a run whose checks all declined to grade, and a run whose checks
  // failed are different things to tell a customer. "Declined to grade" in
  // particular is a claim about the DOMAIN — it must never stand in for our
  // own request failing, and it must never be shown mid-measurement.
  if (scored.length === 0) {
    const states = Object.values(tools)
    const reason = isRunning
      ? 'Your five pillars appear when the audit finishes.'
      : states.some(s => s?.status === 'done')
        ? 'Every completed check declined to grade, so there are no pillars to plot.'
        : states.some(s => s?.status === 'error')
          ? 'The checks behind these pillars did not complete. Re-run to plot them.'
          : 'Your five AI-readiness pillars appear after the first audit.'
    return (
      <div className='flex h-full flex-col justify-center gap-2 px-6 py-7 sm:px-8'>
        <InstrumentLabel tone='secondary'>Signal profile</InstrumentLabel>
        <span className='block text-sm text-muted-foreground'>{reason}</span>
      </div>
    )
  }

  const weakest = scored.reduce((lo, p) => (p.score! < lo.score! ? p : lo))

  const description = `${scored
    .map(p => `${p.label} ${p.score}`)
    .join(', ')}. ${weakest.label} is the weakest signal.`

  return (
    <div className='flex flex-col gap-4 px-6 py-7 sm:px-8'>
      <div className='flex flex-wrap items-start justify-between gap-x-4 gap-y-1'>
        <div className='flex flex-col gap-1'>
          <InstrumentLabel tone='secondary'>Signal profile</InstrumentLabel>
          <span className='block text-sm text-muted-foreground'>
            Your five AI-readiness pillars
          </span>
        </div>
        <span className='flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.12em] text-instrument'>
          <span
            aria-hidden='true'
            className='h-1.5 w-1.5 shrink-0'
            style={{ background: 'var(--primary)' }}
          />
          Current scan
        </span>
      </div>

      <div className='flex justify-center'>
        <svg
          viewBox={`0 0 ${VB_W} ${VB_H}`}
          className='h-auto w-full max-w-[340px]'
          role='img'
          aria-label={`AI readiness signal profile. ${description}`}
        >
          <g aria-hidden='true'>
            {RINGS.map(r => (
              <polygon
                key={r}
                points={polygon(plotted.map(() => r))}
                fill='none'
                stroke='var(--pm-hairline)'
                strokeWidth={1}
              />
            ))}
            {plotted.map((_, i) => {
              const p = point(i, plotted.length, 1)
              return (
                <line
                  key={i}
                  x1={CX}
                  y1={CY}
                  x2={p.x}
                  y2={p.y}
                  stroke='var(--pm-hairline)'
                  strokeWidth={1}
                />
              )
            })}

            <polygon
              points={polygon(plotted.map(p => p.ratio))}
              fill='var(--primary)'
              fillOpacity={0.16}
              stroke='var(--primary)'
              strokeWidth={1.5}
            />

            {/* Node, label and value per pillar. The node takes the grade's
                colour, so the weak corner of the shape is visible without
                reading a number — the same signal the value list carries. */}
            {plotted.map((p, i) => {
              const node = point(i, plotted.length, p.ratio)
              const label = point(i, plotted.length, 1, LABEL_RADIUS)
              const anchor = anchorFor(Math.cos(label.a))
              const colour = p.grade
                ? (GRADE_COLORS[p.grade] ?? 'var(--primary)')
                : 'var(--pm-text-muted)'
              // A label below the centre needs its pair pushed down, not up,
              // or the value would sit on top of the polygon.
              const above = label.y < CY
              return (
                <g key={p.key}>
                  {typeof p.score === 'number' && (
                    <circle cx={node.x} cy={node.y} r={4} fill={colour} />
                  )}
                  <text
                    x={label.x}
                    y={above ? label.y - 4 : label.y + 2}
                    textAnchor={anchor}
                    fontSize='11'
                    fill='var(--pm-text-muted)'
                    fontFamily='var(--font-geist-mono)'
                  >
                    {p.label}
                  </text>
                  <text
                    x={label.x}
                    y={above ? label.y + 12 : label.y + 18}
                    textAnchor={anchor}
                    fontSize='14'
                    fontWeight='600'
                    fill={colour}
                    fontFamily='var(--font-geist-mono)'
                  >
                    {typeof p.score === 'number' ? p.score : '--'}
                  </text>
                </g>
              )
            })}

            {/* Overall score at the centre of the shape. It repeats the figure
                in the score column by design — here it is the reference the
                five pillars are read against, not a second headline. */}
            {overallScore !== null && (
              <>
                <circle
                  cx={CX}
                  cy={CY}
                  r={30}
                  fill='var(--pm-surface-card)'
                  stroke='var(--pm-hairline-strong)'
                  strokeWidth={1}
                />
                <text
                  x={CX}
                  y={CY + 2}
                  textAnchor='middle'
                  fontSize='19'
                  fontWeight='600'
                  fill='currentColor'
                  fontFamily='var(--font-geist-mono)'
                >
                  {overallScore}
                </text>
                <text
                  x={CX}
                  y={CY + 16}
                  textAnchor='middle'
                  fontSize='9'
                  fill='var(--pm-text-muted)'
                  fontFamily='var(--font-geist-mono)'
                  letterSpacing='0.1em'
                >
                  OVERALL
                </text>
              </>
            )}
          </g>
        </svg>
      </div>

      <span className='mt-auto block text-[11px] leading-snug text-instrument'>
        Weakest signal: {weakest.label}. Pillar detail is below.
      </span>
    </div>
  )
}

/**
 * The five pillars as a full-width strip: grade, score, bar, and the tools
 * behind the mean. Clicking one filters the tool table.
 *
 * This IS the category control on the dashboard. It took the job from
 * `CategoryGrid` when the cards were dropped for repeating the hero chart, so
 * removing it without a replacement would leave `filters.category` unreachable
 * — the bar can only clear that filter, never set it.
 */
export function SignalDetailBand({
  tools,
  onCategoryClick,
  activeCategory,
}: {
  tools: Partial<Record<ToolKey, ToolState>>
  onCategoryClick?: (key: string) => void
  activeCategory?: string | null
}) {
  const summaries = summariseCategories(tools)

  return (
    <Panel
      as='section'
      aria-label='Signal health'
      className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5'
    >
      {summaries.map((s, i) => {
        const isActive = activeCategory === s.key
        const colour = s.grade
          ? (GRADE_COLORS[s.grade] ?? 'var(--primary)')
          : 'var(--pm-text-muted)'

        const body = (
          <>
            <span className='flex items-center justify-between gap-2'>
              <InstrumentLabel tone={isActive ? 'secondary' : 'muted'}>
                {s.label}
              </InstrumentLabel>
              <GradeChip grade={s.grade} />
            </span>

            <span
              className='font-display text-[27px] font-semibold leading-none tracking-[-0.02em] tabular-nums'
              style={{ color: colour }}
            >
              {s.score ?? '--'}
            </span>

            {/* An unscored pillar gets an EMPTY track, never a zero-width bar
                styled as a real reading. */}
            <span
              aria-hidden='true'
              className='block h-[3px] w-full bg-surface-raised'
            >
              {s.score !== null && (
                <span
                  className='block h-full'
                  style={{
                    width: `${Math.min(100, Math.max(0, s.score))}%`,
                    background: colour,
                  }}
                />
              )}
            </span>

            {/* The tools behind the mean. It is what makes a pillar score
                auditable rather than an opinion, and it explains a `--`. */}
            <span className='flex flex-col gap-0.5 font-mono text-[10px] text-instrument'>
              {s.members.map(m => (
                <span key={m.key} className='flex justify-between gap-2'>
                  <span className='truncate'>{m.name}</span>
                  <span className='shrink-0 tabular-nums'>
                    {m.score ?? (m.withheld ? 'withheld' : '--')}
                  </span>
                </span>
              ))}
            </span>
          </>
        )

        const cell = [
          'flex w-full flex-col gap-2.5 px-5 py-4 text-left',
          // 2-col: rule after the first cell of each row. 5-col: right rules
          // only, and none on the last cell.
          i % 2 === 0 ? 'border-r border-hairline' : '',
          i < summaries.length - 1 ? 'border-b border-hairline' : '',
          'sm:border-b-0 lg:border-b-0',
          i === summaries.length - 1 ? 'xl:border-r-0' : 'xl:border-r',
          isActive ? 'bg-surface-raised' : '',
        ]
          .filter(Boolean)
          .join(' ')

        if (!onCategoryClick) {
          return (
            <div key={s.key} className={cell}>
              {body}
            </div>
          )
        }
        return (
          <button
            key={s.key}
            type='button'
            onClick={() => onCategoryClick(s.key)}
            aria-label={categoryControlLabel(s)}
            aria-pressed={isActive}
            className={`${cell} transition-colors hover:bg-surface-raised focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-inset`}
          >
            {body}
          </button>
        )
      })}
    </Panel>
  )
}
